import {useMutation, useQuery} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {EDIT_POLICY} from '../../../gql/mutations/policyMutation'
import Select from 'react-select'
import {parseId} from '../../../helpers'
import {IResUpdatePolicyApi} from '../../../types/policies'
import {GET_ALL_LEAD_SOURCES} from '../../../gql/queries/leadSourceQuery'
import {IResLeadSource} from '../../leadSource/leadSourceTypes'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: (vars?: any) => any
  currentSourceId?: string
  currentSourceName?: string
}

/** Llaves consumidas vía `t()` (el extractor no sigue wrappers, §17.12). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  placeholder: {id: 'policies.detail.modal.source.placeholder', defaultMessage: 'Select lead source...'},
  loading: {id: 'policies.detail.modal.source.loading', defaultMessage: 'Loading lead sources...'},
})

export const ModalChangeSourcePolicy: React.FC<IModalProps> = ({
  id,
  isOpen,
  onClose,
  refetch,
  currentSourceId,
  currentSourceName,
}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const errMsg = (message: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const {data: dataLeadSources, loading: loadingLeadSources} = useQuery<IResLeadSource>(
    GET_ALL_LEAD_SOURCES,
    {
      variables: {category: 'Policy', enabled: true},
      fetchPolicy: 'network-only',
    }
  )

  const [gqlUpdatePolicy, {data: dataUpdate, loading, error: errorUpdate}] =
    useMutation<IResUpdatePolicyApi>(EDIT_POLICY)

  const [leadSourceId, setLeadSourceId] = useState<string | null>(null)
  const [selectedValue, setSelectedValue] = useState<{label: string; value: string} | null>(null)
  const [options, setOptions] = useState<{label: string; value: string}[]>([])

  useEffect(() => {
    if (!dataLeadSources) return
    const opts = dataLeadSources.allLeadSources.edges.map(({node}) => ({
      label: node.name,
      value: node.id,
    }))
    setOptions(opts)

    // Set current source as default
    if (currentSourceId) {
      setLeadSourceId(parseId(currentSourceId).toString())
      const current = opts.find((opt) => opt.value === currentSourceId)
      if (current) {
        setSelectedValue(current)
      }
    }
  }, [dataLeadSources, currentSourceId])

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate && dataUpdate.updatePolicy.success) {
      toast.success(
        intl.formatMessage({
          id: 'policies.detail.modal.source.toast.success',
          defaultMessage: 'Source updated successfully',
        })
      )
      refetch({id})
      onClose()
    } else if (dataUpdate.updatePolicy.errors && dataUpdate.updatePolicy.errors.length > 0) {
      toast.error(errMsg(dataUpdate.updatePolicy.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!leadSourceId) {
      toast.error(
        intl.formatMessage({
          id: 'policies.detail.modal.source.selectRequired',
          defaultMessage: 'Please select a lead source',
        })
      )
      return
    }

    gqlUpdatePolicy({
      variables: {
        id: parseId(id),
        policyData: {
          leadSource: leadSourceId,
        },
      },
    })
  }

  useEffect(() => {
    if (errorUpdate) {
      toast.error(errMsg(errorUpdate.message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdate])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage
            id='policies.detail.modal.source.title'
            defaultMessage='Change Lead Source'
          />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formChangeSource' onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>
              <FormattedMessage
                id='policies.detail.modal.source.label'
                defaultMessage='Select the lead source for this policy'
              />
            </Form.Label>
            <Select
              isLoading={loadingLeadSources}
              loadingMessage={() => t('policies.detail.modal.source.loading', 'Loading lead sources...')}
              placeholder={t('policies.detail.modal.source.placeholder', 'Select lead source...')}
              options={options}
              value={selectedValue}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  setSelectedValue(selectedOption)
                  const sourceId = selectedOption.value
                  setLeadSourceId('' + parseId(sourceId))
                }
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button size='sm' variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          size='sm'
          variant='primary'
          type='submit'
          form='formChangeSource'
          formAction='formChangeSource'
          disabled={!leadSourceId || loading}
        >
          {loading && <Spinner animation='border' size='sm' className='me-2' />} <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
